import type { ChatPageNudge, ChatPageOffer, ChatPageRecap } from './chatPageOffer.ts';

/**
 * The one thing a page may put in an empty drawer, in whichever shape it came.
 *
 * `intake` and `debrief` are questions Wundo asks — one before the conversation,
 * one after it; `recap` is what was said last time. They are drawn differently
 * and the first answer to each goes to its own door, but everything between
 * those two ends is the same: shown and not stored, standing only while the
 * drawer is blank, and answered exactly once per page.
 *
 * `nudge` is the odd one and ranks last: it is not about this page's meeting at
 * all but about the person reading it, so it opens no conversation, is answered
 * by nobody, and is simply taken away when it is refused or done.
 *
 * `key` is what "once" is counted by — the shape as well as the meeting, so an
 * offer that changes shape is a new opening rather than an answered one.
 */
export type PageOpening =
  | {
      /**
       * One shape for both questions: a line the server wrote, drawn as Wundo's
       * own bubble. Only the door the first answer goes through tells them
       * apart, and that is the one thing the kind is read for.
       */
      kind: 'intake' | 'debrief';
      key: string;
      meetingId: string;
      conversationId: string | null;
      opener: string;
    }
  | {
      kind: 'recap';
      key: string;
      meetingId: string;
      conversationId: string | null;
      recap: ChatPageRecap;
    }
  | {
      kind: 'nudge';
      key: string;
      nudge: ChatPageNudge;
    };

/** The two openings that create a conversation, apart from the nudge that does not. */
export type ConversationalOpening = Exclude<PageOpening, { kind: 'nudge' }>;

/** A conversational opening being answered right now, by the user it is for. */
export type AnsweringOpening = ConversationalOpening & { userId: string };

/**
 * What this page opens an empty drawer with, or null where it opens with
 * nothing. Never more than one: the question wins over the summary, and both
 * win over a recommendation — what Wundo needs to know about the meeting on
 * screen is worth more than what the user has not tried yet.
 */
export function pageOpening(offer: ChatPageOffer | null): PageOpening | null {
  if (offer?.intake) {
    return {
      kind: 'intake',
      key: `intake:${offer.intake.meetingId}`,
      meetingId: offer.intake.meetingId,
      conversationId: offer.intake.conversationId,
      opener: offer.intake.opener,
    };
  }

  if (offer?.debrief) {
    return {
      kind: 'debrief',
      key: `debrief:${offer.debrief.meetingId}`,
      meetingId: offer.debrief.meetingId,
      conversationId: offer.debrief.conversationId,
      opener: offer.debrief.opener,
    };
  }

  if (offer?.recap) {
    return {
      kind: 'recap',
      key: `recap:${offer.recap.meetingId}`,
      meetingId: offer.recap.meetingId,
      conversationId: offer.recap.conversationId,
      recap: offer.recap,
    };
  }

  if (offer?.nudge) {
    return {
      kind: 'nudge',
      key: `nudge:${offer.nudge.key}:${offer.nudge.meetingId ?? ''}`,
      nudge: offer.nudge,
    };
  }

  return null;
}
